/**
 * EMS Stickers Management Page - Simplified overview for HYB MASTER SHOP
 * Shows remaining sticker counts and provides a minimal import flow.
 */

import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Package2, Plus, AlertTriangle, RefreshCw, CheckCircle, MinusCircle, Trash2 } from 'lucide-react';
import { createStickerBatch, fetchStickerInventory, deleteStickers } from '../api/ems';
import { PageHeader } from '../components/ui/PageHeader';
import { Surface } from '../components/ui/Surface';
import { EmptyState } from '../components/ui/EmptyState';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

const FALLBACK_THRESHOLD = 50;

interface ImportStickersFormValues {
  stickerCodes: string;
}

function formatCount(value?: number): string {
  return (value ?? 0).toLocaleString('th-TH');
}

function formatDate(value?: string): string {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('th-TH', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
}

export function EMSStickers() {
  const queryClient = useQueryClient();
  const { logout } = useAuth();

  const [selectedStickers, setSelectedStickers] = useState<string[]>([]);

  const {
    data: inventory,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ems-sticker-inventory'],
    queryFn: fetchStickerInventory,
    staleTime: 60_000,
    onError: (err: unknown) => {
      if (isAxiosError(err) && err.response?.status === 403) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
        logout();
      }
    },
  });

  useEffect(() => {
    if (!inventory?.nextAvailable) {
      setSelectedStickers([]);
      return;
    }
    const availableIds = new Set(inventory.nextAvailable.map(item => item.id));
    setSelectedStickers(prev => prev.filter(id => availableIds.has(id)));
  }, [inventory?.nextAvailable]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ImportStickersFormValues>({
    defaultValues: { stickerCodes: '' },
  });

  const importStickersMutation = useMutation({
    mutationFn: createStickerBatch,
    onSuccess: () => {
      toast.success('นำเข้าสติกเกอร์สำเร็จ');
      queryClient.invalidateQueries({ queryKey: ['ems-sticker-inventory'] });
      reset();
    },
    onError: (err: unknown) => {
      logger.error('import stickers error', err);
      if (isAxiosError(err) && err.response?.status === 403) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
        logout();
        return;
      }
      toast.error('ไม่สามารถนำเข้าสติกเกอร์ได้');
    },
  });

  const onSubmit = handleSubmit(values => {
    const codes = values.stickerCodes
      .split(/\n|\r/)
      .map(code => code.trim())
      .filter(Boolean);

    if (codes.length === 0) {
      toast.error('กรุณากรอกรหัสสติกเกอร์อย่างน้อย 1 รายการ');
      return;
    }

    importStickersMutation.mutate({
      stickerCodes: codes,
    });
  });

  const lowStockAlert = inventory?.lowStockAlert ?? false;
  const alertThreshold = inventory?.alertThreshold ?? FALLBACK_THRESHOLD;

  const nextAvailable = inventory?.nextAvailable ?? [];
  const hasSelection = selectedStickers.length > 0;

  const deleteStickersMutation = useMutation({
    mutationFn: deleteStickers,
    onSuccess: result => {
      if (result.deletedCount > 0) {
        toast.success(`ลบสติกเกอร์ ${result.deletedCount.toLocaleString('th-TH')} ใบสำเร็จ`);
      }
      if (result.errorCount > 0) {
        const firstError = result.errors[0];
        const label = firstError?.code ?? firstError?.id ?? 'บางรายการ';
        toast.error(`ไม่สามารถลบสติกเกอร์ ${label} ได้ (${firstError?.error ?? 'ไม่ทราบสาเหตุ'})`);
      }
      setSelectedStickers([]);
      queryClient.invalidateQueries({ queryKey: ['ems-sticker-inventory'] });
    },
    onError: (err: unknown) => {
      logger.error('delete stickers error', err);
      if (isAxiosError(err) && err.response?.status === 403) {
        toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
        logout();
        return;
      }
      toast.error('ไม่สามารถลบสติกเกอร์ได้');
    },
  });

  const toggleSelection = (id: string) => {
    setSelectedStickers(prev =>
      prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]
    );
  };

  const summaryCards = [
    {
      label: 'พร้อมใช้งาน',
      value: formatCount(inventory?.available),
      icon: <CheckCircle className="h-5 w-5 text-green-500" />,
      highlight: lowStockAlert,
      helper: `ควรเติมเมื่อเหลือต่ำกว่า ${alertThreshold.toLocaleString('th-TH')} ใบ`,
    },
    {
      label: 'ถูกใช้แล้ว',
      value: formatCount(inventory?.used),
      icon: <MinusCircle className="h-5 w-5 text-gray-500" />,
    },
  ];

  const content = (
    <>
      <PageHeader
        title="จัดการสติกเกอร์ EMS"
        description="ดูจำนวนสติกเกอร์คงเหลือและนำเข้าสติกเกอร์ใหม่แบบรวดเร็ว"
        leading={<Package2 className="h-5 w-5" />}
        actions={(
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            รีเฟรชข้อมูล
          </button>
        )}
      />

      {error && (
        <EmptyState
          icon={<AlertTriangle className="h-5 w-5" />}
          title="ไม่สามารถโหลดข้อมูลคงเหลือของสติกเกอร์ได้"
          description="กรุณาลองรีเฟรชอีกครั้ง หรือแจ้งทีมดูแลระบบหากปัญหายังคงอยู่"
          actions={(
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
              ลองอีกครั้ง
            </button>
          )}
        />
      )}

      {!error && (
        <Surface padding="lg" className="space-y-6">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <RefreshCw className="h-4 w-4 animate-spin" /> กำลังโหลดข้อมูลคงเหลือ...
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">สติกเกอร์ทั้งหมดในระบบ</p>
                  <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                    {formatCount(inventory?.total)}
                  </p>
                </div>
                {lowStockAlert && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                    <AlertTriangle className="h-4 w-4" />
                    สติกเกอร์พร้อมใช้งานต่ำกว่า {alertThreshold.toLocaleString('th-TH')} ใบ
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-2">
                {summaryCards.map(card => (
                  <div
                    key={card.label}
                    className={`flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 ${card.highlight ? 'ring-2 ring-amber-400/70' : ''}`}
                  >
                    <div className="rounded-full bg-yellow-50 p-3 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-200">
                      {card.icon}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                      <p className="text-2xl font-semibold text-gray-900 dark:text-white">{card.value}</p>
                      {card.helper && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{card.helper}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Surface>
      )}

      <Surface padding="lg" className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">นำเข้าสติกเกอร์ EMS</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            ป้อนเลขสติกเกอร์ทีละบรรทัด หรือคัดลอกจาก Excel แล้ววางที่นี่ ระบบจะบันทึกเป็นสติกเกอร์พร้อมใช้งานอัตโนมัติ
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              เลขสติกเกอร์ (พิมพ์ทีละ 1 เลขต่อบรรทัด)
            </label>
            <textarea
              {...register('stickerCodes', { required: true })}
              rows={6}
              placeholder={"EMS12345678TH\nEMS12345679TH"}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-400 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            {errors.stickerCodes && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">กรุณากรอกรหัสสติกเกอร์</p>
            )}
          </div>

          <button
            type="submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={importStickersMutation.isLoading}
          >
            {importStickersMutation.isLoading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            นำเข้าสติกเกอร์
          </button>
        </form>
      </Surface>

      <Surface padding="lg" className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">สติกเกอร์พร้อมใช้งานถัดไป (FIFO)</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              ระบบจะเลือกใช้สติกเกอร์ที่นำเข้าก่อนเสมอ
            </p>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            แสดง {nextAvailable.length} รายการ
          </span>
        </div>

        {nextAvailable.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ยังไม่มีสติกเกอร์พร้อมใช้งานในระบบ กรุณานำเข้าสติกเกอร์ใหม่
          </p>
        ) : (
          <>
            <div className="flex items-center justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-60 dark:border-red-700/60 dark:bg-red-900/30 dark:text-red-200 dark:hover:bg-red-900/40"
                onClick={() => deleteStickersMutation.mutate(selectedStickers)}
                disabled={!hasSelection || deleteStickersMutation.isLoading}
              >
                {deleteStickersMutation.isLoading ? (
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
                ลบสติกเกอร์ที่เลือก
              </button>
            </div>
            <ol className="space-y-2">
              {nextAvailable.map((item, index) => (
                <li
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                >
                  <label className="flex w-full items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-yellow-500 focus:ring-yellow-400 dark:focus:ring-orange-500"
                        checked={selectedStickers.includes(item.id)}
                        onChange={() => toggleSelection(item.id)}
                      />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {index + 1}. {item.stickerCode}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      นำเข้าเมื่อ {formatDate(item.createdAt)}
                    </span>
                  </label>
                </li>
              ))}
            </ol>
          </>
        )}
      </Surface>
    </>
  );

  return (
    <div className="space-y-6">
      {content}
    </div>
  );
}
